/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { Box, Grid, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CustomInputLabel from 'components/cmn/CustomInputLabel';
import CustomOutlinedInput from 'components/cmn/CustomOutlinedInput';
import LoginButton from 'components/login/LoginButton';
import { formFields } from 'libs/mindUtils';
import CustomBox from 'components/cmn/CustomBox';
import { useAppDispatch } from 'store';
import { closeDialog, openDialog } from 'store/dialog/dialogSlice';
import ConfirmDialog from 'components/cmn/ConfirmDialog';
import { postCompJoin } from 'store/login/loginSlice';
import { closeBackdrop, openBackdrop } from 'store/backdrop/backdropSlice';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CustomTooltip from 'components/cmn/CustomTooltip';
import { SignUpTooltipFieldId } from 'interface/login/loginInfo';
import { showMessage } from 'store/message/messageSlice';

function TooltipContent({ fieldId }: { fieldId: SignUpTooltipFieldId }) {
  if (fieldId === 'compName') {
    return <Box>소속기업이 없을 경우 &apos;전문컨설턴트&apos;로 작성해주세요</Box>;
  }
  if (fieldId === 'compRole') {
    return (
      <Stack direction="column" spacing={1}>
        <Box>직급과 직책을 /로 구분해서 입력해주세요</Box>
        <Box>작성이 어려운 경우 &apos;전문컨설턴트&apos;로 작성해주세요</Box>
      </Stack>
    );
  }
  return null;
}

function SignUpForm(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const {
    handleSubmit,
    formState: { errors },
    trigger,
    control,
    getValues,
  } = useForm();

  const handleSignUpSuccess = () => {
    dispatch(
      openDialog({
        content: (
          <ConfirmDialog buttonText="로그인" contents="회원가입이 완료되었습니다." handleClick={handleMovePage} />
        ),
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
      }),
    );
  };
  const onSubmit = (_data: any) => {
    dispatch(openBackdrop({}));
    dispatch(postCompJoin(_data))
      .unwrap()
      .then(response => {
        const { status } = response;
        if (status === 200) {
          handleSignUpSuccess();
        } else {
          dispatch(
            showMessage({ message: response?.data?.message || '회원가입에 실패하였습니다.', variant: 'warning' }),
          );
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };
  const handleClickShowPassword = (id: string) => {
    if (id === 'password') {
      setShowPassword(!showPassword);
    } else if (id === 'confirmPassword') {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };
  const handleTextType = (fieldId: string, fieldType: string): string => {
    if (fieldId === 'password') {
      return showPassword ? 'text' : 'password';
    }
    if (fieldId === 'confirmPassword') {
      return showConfirmPassword ? 'text' : 'password';
    }
    return fieldType;
  };
  const handleEndAdornment = (fieldId: string) => {
    if (['password', 'confirmPassword'].includes(fieldId)) {
      return (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle password visibility"
            onClick={() => handleClickShowPassword(fieldId)}
            edge="end"
            size="small"
          >
            {(fieldId === 'password' && showPassword) || (fieldId === 'confirmPassword' && showConfirmPassword) ? (
              <Visibility />
            ) : (
              <VisibilityOff />
            )}
          </IconButton>
        </InputAdornment>
      );
    }
    return null;
  };
  const handleMovePage = () => {
    navigate('/login');
    dispatch(closeDialog());
  };

  const signUpValues = useWatch({
    control,
    defaultValue: {
      name: '',
      email: '',
      mobile: '',
      password: '',
      confirmPassword: '',
      compName: '',
      compRole: '',
    },
  });

  useEffect(() => {
    const handleValidate = Object.keys(signUpValues).filter(field => signUpValues[field].length > 0);

    if (handleValidate.length > 0) {
      trigger(handleValidate);
    }
  }, [signUpValues, trigger]);

  const isDisabled = useMemo(
    () => Object.keys(signUpValues).some(field => signUpValues[field].length === 0),
    [signUpValues],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container>
        {formFields.map(field => (
          <Grid item xs={12} mb={2.5} key={field.id}>
            <Grid item xs={12} key={field.id}>
              <Stack spacing={1.25}>
                <Controller
                  name={field.id}
                  control={control}
                  defaultValue=""
                  rules={{
                    required: field.required,
                    validate:
                      field.id === 'confirmPassword'
                        ? value => value === getValues('password') || '비밀번호가 일치하지 않습니다.'
                        : field.validate,
                  }}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <CustomInputLabel
                        htmlFor={field.id}
                        label={field.label}
                        essential={field.required}
                        tooltip={
                          field.tooltip && (
                            <CustomTooltip
                              title={<TooltipContent fieldId={field.id} />}
                              placement="top"
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    fontSize: '0.85rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    p: 2,
                                    maxWidth: 310,
                                  },
                                },
                              }}
                            >
                              <IconButton size="small" sx={{ color: 'mind.black.A10', p: 0, ml: 1 }}>
                                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </CustomTooltip>
                          )
                        }
                      />
                      <CustomOutlinedInput
                        id={field.id}
                        value={value}
                        onChange={e => {
                          let tmpValue = e.target.value;
                          if (field.id === 'mobile') {
                            tmpValue = tmpValue.replace(/[^0-9]/g, '');
                            tmpValue = tmpValue.replace(/-/g, '');
                            tmpValue = tmpValue.substring(0, 11);
                          }
                          onChange(tmpValue);
                        }}
                        placeholder={field.placeholder}
                        fullWidth
                        error={!!errors[field.id]}
                        message={(errors[field.id]?.message as string) || ''}
                        type={handleTextType(field.id, field.type)}
                        autoComplete="off"
                        endAdornment={handleEndAdornment(field.id)}
                      />
                    </>
                  )}
                />
              </Stack>
            </Grid>
          </Grid>
        ))}
        <Grid item xs={12}>
          <LoginButton disabled={isDisabled || Object.keys(errors).length > 0}>회원가입</LoginButton>
        </Grid>
        <Grid item xs={12} mt={2} justifyContent="flex-start" sx={{ display: 'flex', width: '100%' }}>
          <Stack direction="row" alignItems="center" sx={{ flex: 1, justifyContent: 'space-between' }}>
            <Typography sx={{ color: 'mind.grey.alert', typography: 's14w400' }}>이미 계정이 있으신가요?</Typography>
            <CustomBox title="로그인" decoration="underline" onClick={handleMovePage} />
          </Stack>
        </Grid>
      </Grid>
    </form>
  );
}

export default memo(SignUpForm);
